import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { graphql } from "gatsby";
import React, { FC } from "react";

import { Cta } from "../../buttons/cta";
import { Icon } from "../../icon";
import { RichText } from "../../rich-text";

export interface AccordionItemProps extends Queries.AccordionItemDataFragment {
  isOpen: boolean;
  onOpen: () => void;
}

export const AccordionItem: FC<AccordionItemProps> = ({
  title,
  description,
  ctas,
  isOpen,
  onOpen,
}) => {
  return (
    <div>
      <div
        className={clsx(
          "flex items-center justify-between bg-neutral py-4",
          !isOpen && "cursor-pointer"
        )}
        onClick={onOpen}
      >
        <h4 className="font-medium text-xl">{title}</h4>
        <div className={clsx("transition-all duration-300", !isOpen && "rotate-180")}>
          <Icon
            className={clsx("w-4", !isOpen && "text-primary")}
            icon={isOpen ? "chevron-up" : "chevron-up-thick"}
          />
        </div>
      </div>

      <AnimatePresence mode="popLayout">
        {description?.raw && isOpen && (
          <motion.div
            className="text-lg origin-top pb-6"
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -24, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <RichText key="description" raw={description.raw} />
            {ctas?.length && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="w-fit mt-4 flex gap-4 items-center"
              >
                {ctas.filter(Boolean).map((cta, i) => (
                  <Cta
                    key={i}
                    variant={i === ctas.length - 1 ? "primary" : "primary-outline"}
                    {...cta!}
                    size="sm"
                    fullWidth
                  />
                ))}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const query = graphql`
  fragment AccordionItemData on ContentfulAccordionItem {
    title
    description {
      raw
    }
    ctas {
      ...CtaData
    }
  }
`;
